import React, { useEffect, useState, useMemo } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  companyDeleteUserIdAction,
  companyUserListAction,
} from "../redux/actions/company_user_action";
import { ContentCard } from "components/ContentCard";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import { UserListGrid } from "../components/UserListGrid";
import companyUserApi from "services/apis/companyUserApi";
import { jsonToFormData } from "utils/ServicesUtils";
import ToastUtils from "utils/ToastUtils";
import { getPathPrefix } from "utils/coreUtils";
import { UserFormTypes } from "constants/index";
import "../styles/style.scss";

const UserListPage = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const companyUserId = useMemo(
    () => localStorage.getItem("companyUserId"),
    []
  );
  const [search, setSearch] = useState("");
  const datastreamList = useSelector((state) => state?.datastream?.list);
  const companyUserListData = useSelector(
    (state) => state.companyUserReducer.companyUserListData
  );
  const isLoading = useSelector((state) => state.companyUserReducer.isLoading);
  const [t] = useTranslation();

  const getCompanyUserList = () => {
    dispatch(
      companyUserListAction({
        name: search,
      })
    );
  };

  useEffect(() => {
    setSearch("");
    getCompanyUserList();
  }, [dispatch, searchParams.get("t")]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getCompanyUserList();
    }, 500);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [search]);

  const checkHandler = async (companyUserId, status) => {
    try {
      const formData = jsonToFormData({
        companyUserId,
        isActive: status,
      });

      const response = await companyUserApi.updateCompanyProfile(formData);

      if (response.data.status != 200) {
        toast.error(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success("Status updated");
      }

      getCompanyUserList();
    } catch (error) {
      /* empty */
    }
  };

  const handleDeleteUser = async (index) => {
    await dispatch(companyDeleteUserIdAction(index))
      .then((res) => {
        getCompanyUserList();
        // toast.success("User deleted");
        if (res.status != 200) {
          ToastUtils.serverError(t(`statusCodes.${res?.code}`));
        } else {
          toast.success(t(`statusCodes.${res?.code}`));
        }
      })
      .catch(() => {
        // toast.error(e.data?.message);
      });
  };

  const getConfigNames = (configIds) => {
    return datastreamList
      ?.filter((e) => configIds.includes(e.dataStreamConfigId))
      .map((e) => e.customDataStreamName)
      .join(", ");
  };

  const companyAdminList = useMemo(() => {
    const finalData = companyUserListData;
    return finalData.filter((x) => x.userRoleType === "CA");
  }, [companyUserListData]);

  const companyUserList = useMemo(() => {
    const finalData = companyUserListData;
    return finalData.filter((x) => x.userRoleType === "CU");
  }, [companyUserListData]);

  return (
    <>
      <AiccBreadCrumb
        title={t("userList.mainBreadcrumbTitle")}
        items={[
          {
            name:
              searchParams.get("t") === "ru"
                ? t("userList.routingBreadcrumbName")
                : t("userList.routingUserBreadcrumbName"),
          },
        ]}
      />
      <ContentCard
        headerImgSrc="/assets/images/user.svg"
        title={
          searchParams.get("t") === "ru"
            ? t("userList.routingTitle")
            : t("userList.title")
        }
        headerContent={
          <div className="d-flex justify-content-between">
            <NavLink
              to={getPathPrefix(
                `company-user/create-user?t=${UserFormTypes.CREATE_USER}`
              )}
              type="button"
              className="btn btn-main-primary"
            >
              <i className="fa-solid fa-plus me-2"></i>
              {t("userList.addUserButtonTitle")}
            </NavLink>
            <a
              type="button"
              className="btn ms-md-3 btn-main-primary"
              onClick={() => {
                getCompanyUserList();
              }}
            >
              <i className="fa-sharp fa-solid fa-rotate me-2"></i>
              {t("userList.refreshUserButtonTitle")}
            </a>
          </div>
        }
        bodyContent={
          <>
            <div className="form pb-3">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("userList.searchHint")}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <UserListGrid
                userListKey="company-admin"
                listTitle={t("userList.adminUsers")}
                list={companyAdminList}
                toggleActiveStatusHandler={checkHandler}
                loggedInUserId={companyUserId}
                handleDeleteUser={handleDeleteUser}
                getConfigNames={getConfigNames}
                noUserMessage={t("userList.noCompanyAdminFound")}
              />
              <hr />
              <UserListGrid
                userListKey="company-user"
                listTitle={t("userList.companyUsers")}
                list={companyUserList}
                toggleActiveStatusHandler={checkHandler}
                loggedInUserId={companyUserId}
                handleDeleteUser={handleDeleteUser}
                getConfigNames={getConfigNames}
                noUserMessage={t("userList.noCompanyUserFound")}
              />
            </div>
          </>
        }
      />
      <div className="d-sm-flex justify-content-center">
        {isLoading && (
          <img
            src={"/assets/images/loader_gif.gif"}
            height={"50"}
            width={"50"}
            align={"center"}
          />
        )}
      </div>
    </>
  );
};

export default UserListPage;
